import { ItemPaging, FavoriteChannels, type ProtoItem } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { Services } from "@ca.vmedia.tv/services";
import { Logger } from '@/utils';
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { ExposeInterface } from './types';
import type { AxiosError } from 'axios';
import { useApplicationStore } from "@/stores/Application";

export const useFavoriteChannelsStore = defineStore('FavoriteChannels', (): ExposeInterface => {
    const { 
        emptyFavoriteChannels
    } = useEmptyLine();

    const ApplicationStore = useApplicationStore();
    const {
        getCurrentTime: currentTime,
    } = storeToRefs(ApplicationStore);

    let _numberPage: number = 1;

    const _favoriteChannels: Ref<FavoriteChannels | null> = ref(null);
    const getFavoriteChannels = computed((): FavoriteChannels | null => _favoriteChannels.value);

    /** 
    * Request programs for favorite channels.
    * @param {FavoriteChannels} favoriteChannels - favorite channels for which we want to fetch programs   
    * @returns void
    */
    const fetchFavoriteChannelsPrograms = async (favoriteChannels: FavoriteChannels): Promise<void> => {
        if (!favoriteChannels?.items || favoriteChannels.items.length === 0) {
            return;
        } 
        const channelIds: number[] = favoriteChannels.items.map((channel: ProtoItem) => channel.id ?? -1);
        await Services.Instance.guide.getSegmentFromCache(channelIds, currentTime.value);
    };
    const fetchFavoriteChannels = async (): Promise<void> => {
        try {
            const favoriteChannels = await Services.Instance.domains.favoritesRepository.getFavoritesChannels(1, AppConstants.ItemsInLine);
            if (!favoriteChannels?.items) {
                throw new Error('Favorite channels is empty or response is empty');
            }

            await fetchFavoriteChannelsPrograms(favoriteChannels);

            _favoriteChannels.value = favoriteChannels; //Need to last because we need to fetch programs for new channels
        } catch (error) {
            _favoriteChannels.value = emptyFavoriteChannels();
            Logger.captureError(error as AxiosError);
        }
    };

    const fetchFavoriteChannelsNextPage = async (): Promise<void> => {
        try {
            _numberPage++;
            
            const response = await Services.Instance.domains.favoritesRepository.getFavoritesChannels(_numberPage, AppConstants.ItemsInLine);
            if (!_favoriteChannels.value?.items || !response.items) {
                throw new Error('Favorite channels is empty or response is empty');
            }
            const items = [..._favoriteChannels.value.items, ...response.items];
            const paging = new ItemPaging(response.paging?.itemsOnPage, _numberPage, response.paging?.totalItems, response.paging?.totalPages);
            const favoriteChannels = new FavoriteChannels(items, paging);

            await fetchFavoriteChannelsPrograms(favoriteChannels);

            _favoriteChannels.value = favoriteChannels; //Need to last because we need to fetch programs for new channels
        } catch (error) {
            Logger.captureError(error as Error | AxiosError);
        }
    };

    const _favoriteChannelsIds: Ref<number[]> = ref([]);
    const getFavChannelsIds = computed((): number[] => _favoriteChannelsIds.value);
    const fetchFavChannelsIds = async (): Promise<void> => {
        try {
            const response = await Services.Instance.domains.favoritesRepository.getFavoritesChannels(1, 999);
            if (!response?.items) {
                throw new Error('Favorite channels is empty or response is empty');
            }
            const channelIds: number[] = response.items.map((channel: ProtoItem) => channel.id ?? -1);
            _favoriteChannelsIds.value = channelIds;
        } catch (error) {
            Logger.captureError(error as Error | AxiosError);
        }
    };

    const _deletedIds: Ref<Set<string>> = ref(new Set());
    const getDeletedIdsChannels: ComputedRef<Set<string>> = computed(() => _deletedIds.value);
    const saveDeletedIds = (id: string): void => {
        _deletedIds.value.add(id);
    };
    const clearDeletedIds = (id: string): void => {
        if(_deletedIds.value.has(id)) {
            _deletedIds.value.delete(id);
        }
    }
    const clearAllDeletedIds = (): void => {
        _deletedIds.value.clear();
    }

    const clearHard = (): void => {
        _numberPage = 1;
        _favoriteChannels.value = null;
        _favoriteChannelsIds.value = [];
    };

    const clearSoft = (): void => {
        _numberPage = 1;
        if(!_favoriteChannels.value?.items) {
            throw new Error('Favorite channels is empty');
        }
        const items = [..._favoriteChannels.value.items].slice(0, AppConstants.ItemsInLine);
        if (!_favoriteChannels.value.paging) {
            throw new Error('Favorite channels paging is empty');
        }
        const paging = new ItemPaging(_favoriteChannels.value.paging.itemsOnPage, _numberPage, _favoriteChannels.value.paging.totalItems, _favoriteChannels.value.paging.totalPages);
        _favoriteChannels.value = new FavoriteChannels(items, paging);
    };


    return {
        getDeletedIdsChannels,
        getFavoriteChannels,
        getFavChannelsIds,
        fetchFavChannelsIds,
        fetchFavoriteChannels,
        fetchFavoriteChannelsNextPage,
        saveDeletedIds,
        clearDeletedIds,
        clearAllDeletedIds,
        clearHard,
        clearSoft
    };
});